// frontend/src/components/CookieConsent.tsx
//
// PRIVACY-CONSENT-01 — Cookie consent banner.
// Quebec Loi 25 + GDPR Art. 7 + ePrivacy Directive compliant.
//
// Key compliance requirements this component enforces:
//   - Explicit affirmative action (NO pre-ticked boxes; user must click)
//   - Granular per-category toggles (necessary | analytics | marketing)
//   - EQUAL prominence of Accept-All and Reject-All (same size, color weight)
//   - Clear purpose description + link to Privacy Policy
//   - Withdrawable at any time (handled by PrivacySection in Settings)
//   - No tracking before consent (marketingAttribution.ts checks hasConsentFor)
//
// UX: sits in flow below the desktop header and docks above the mobile safe area.
// Sticky until the user decides, without covering the first product viewport.
// After decision, hidden via `isDecided`. Can be re-opened via the
// Settings → Security → Privacy Preferences section.
//
// i18n: all strings go through t() per frontend-conventions.md.

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, ChevronRight, Check, X } from 'lucide-react';
import { useConsent, ConsentCategories } from '../hooks/useConsent';
import { useIsMobile } from '../hooks/useMediaQuery';

export const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { isDecided, state, acceptAll, rejectAll, saveGranular } = useConsent();

  const [mode, setMode] = useState<'banner' | 'customize'>('banner');
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<ConsentCategories>(() => ({
    necessary: true,
    analytics: state?.categories.analytics ?? false,
    marketing: state?.categories.marketing ?? false,
  }));

  // Respect prefers-reduced-motion — skip slide animation
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const listener = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
  }, []);

  const animation = prefersReducedMotion ? 'none' : 'consentFadeIn 220ms ease-out';
  const desktopBanner = !isMobile && mode === 'banner';
  const compactBanner = mode === 'banner';
  const compactMobileBanner = isMobile && mode === 'banner';

  // Don't render if user has already made a decision (unless they re-opened via settings)
  if (isDecided) return null;

  const handleAcceptAll = async () => {
    setBusy(true);
    try {
      await acceptAll();
    } finally {
      setBusy(false);
    }
  };

  const handleRejectAll = async () => {
    setBusy(true);
    try {
      await rejectAll();
    } finally {
      setBusy(false);
    }
  };

  const handleSaveGranular = async () => {
    setBusy(true);
    try {
      await saveGranular(draft);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`xp-consent xp-consent--${mode}`}
      role="region"
      aria-label={t('cookieConsent.title', 'Privacy Preferences')}
      aria-describedby={mode === 'banner'
        ? 'cookie-consent-description'
        : 'cookie-consent-customize-description'}
      style={{
        position: desktopBanner ? 'sticky' : 'fixed',
        top: desktopBanner ? 'calc(76px + env(safe-area-inset-top, 0px))' : 'auto',
        bottom: desktopBanner ? 'auto' : isMobile ? 'calc(8px + env(safe-area-inset-bottom, 0px))' : '16px',
        left: isMobile ? '8px' : desktopBanner ? 'auto' : '20px',
        right: isMobile ? '8px' : 'auto',
        width: desktopBanner ? 'calc(100% - 40px)' : 'auto',
        maxWidth: isMobile ? 'calc(100% - 16px)' : mode === 'customize' ? '500px' : '980px',
        background: 'var(--bg-secondary)',
        borderRadius: '12px',
        border: '1px solid var(--border-primary)',
        boxShadow: '0 -6px 24px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.04)',
        padding: compactMobileBanner ? '7px 8px' : desktopBanner ? '6px 10px' : isMobile ? '10px' : '12px',
        zIndex: 9999,
        animation: desktopBanner ? 'none' : animation,
        transform: 'none',
        margin: desktopBanner ? '0 auto' : undefined,
        color: 'var(--text-primary)',
        maxHeight: mode === 'customize' ? 'min(80vh, 560px)' : 'none',
        overflowY: mode === 'customize' ? 'auto' : 'visible',
        boxSizing: 'border-box',
        display: desktopBanner ? 'grid' : 'block',
        gridTemplateColumns: desktopBanner ? 'minmax(0, 1fr) auto' : undefined,
        gridTemplateRows: desktopBanner ? 'auto' : undefined,
        columnGap: desktopBanner ? '16px' : undefined,
      }}
    >
      <style>{`
        @keyframes consentFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .xp-consent-toggle {
          appearance: none;
          width: 44px;
          height: 24px;
          background: var(--bg-tertiary);
          border-radius: 12px;
          position: relative;
          cursor: pointer;
          border: 1px solid var(--border-primary);
          transition: background 180ms ease, border-color 180ms ease;
        }
        .xp-consent-toggle::after {
          content: '';
          position: absolute;
          top: 2px;
          left: 2px;
          width: 18px;
          height: 18px;
          background: white;
          border-radius: 50%;
          transition: transform 180ms ease;
        }
        .xp-consent-toggle:checked {
          background: var(--primary-500);
          border-color: var(--primary-500);
        }
        .xp-consent-toggle:checked::after {
          transform: translateX(20px);
        }
        .xp-consent-toggle:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .xp-consent-toggle:focus-visible {
          outline: 2px solid var(--primary-400);
          outline-offset: 2px;
        }
        @media (max-width: 767px) {
          .xp-consent--banner {
            position: fixed !important;
            inset: auto 8px calc(8px + env(safe-area-inset-bottom, 0px)) !important;
            width: auto !important;
            max-width: calc(100% - 16px) !important;
            margin: 0 !important;
            padding: 7px 8px !important;
            display: block !important;
            overflow-x: hidden !important;
          }
          .xp-consent-actions {
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
            gap: 6px !important;
          }
          .xp-consent-action {
            min-width: 0 !important;
            padding-inline: 3px !important;
            line-height: 1.15 !important;
            overflow-wrap: anywhere;
            white-space: normal;
          }
          .xp-consent-action svg {
            display: none;
          }
        }
      `}</style>

      {/* Header */}
      <div
        style={{
          display: compactBanner ? 'none' : 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: desktopBanner ? 0 : '4px',
          gridColumn: desktopBanner ? '1' : undefined,
          gridRow: desktopBanner ? '1' : undefined,
        }}
      >
        <Shield size={isMobile ? 15 : desktopBanner ? 15 : 17} color="var(--primary-500)" aria-hidden="true" />
        <div
          id="cookie-consent-title"
          style={{
            fontSize: isMobile ? '14px' : desktopBanner ? '13px' : '15px',
            fontWeight: 600,
            margin: 0,
            color: 'var(--text-primary)',
          }}
        >
          {t('cookieConsent.title', 'Privacy Preferences')}
        </div>
      </div>

      {mode === 'banner' && (
        <>
          <p
            id="cookie-consent-description"
            style={{
              display: compactBanner ? 'flex' : undefined,
              alignItems: compactBanner ? 'flex-start' : undefined,
              alignSelf: desktopBanner ? 'center' : undefined,
              gap: compactBanner ? '6px' : undefined,
              fontSize: compactMobileBanner ? '10.5px' : isMobile ? '12px' : desktopBanner ? '11px' : '12px',
              lineHeight: compactMobileBanner ? 1.25 : isMobile ? 1.4 : desktopBanner ? 1.2 : 1.35,
              color: 'var(--text-secondary)',
              margin: desktopBanner ? 0 : compactMobileBanner ? '0 0 6px' : '0 0 10px 0',
              gridColumn: desktopBanner ? '1' : undefined,
              gridRow: desktopBanner ? '1' : undefined,
            }}
          >
            {compactBanner && (
              <Shield size={13} color="var(--primary-500)" aria-hidden="true" style={{ flexShrink: 0, marginTop: '1px' }} />
            )}
            <span>
              {t(
                'cookieConsent.descriptionCompact',
                'Essential cookies. Optional: your choice.'
              )}{' '}
              <a
                href="https://www.xprojex.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'var(--accent-text)',
                  textDecoration: 'underline',
                }}
              >
                {t('cookieConsent.learnMore', 'Learn more')}
              </a>
            </span>
          </p>

          {/* Buttons — equal prominence per Loi 25 Art. 7.2 */}
          <div
            className="xp-consent-actions"
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(3, minmax(0, 1fr))' : '1fr 1fr 1fr',
              gap: isMobile ? '6px' : '8px',
              marginBottom: 0,
              width: desktopBanner ? '360px' : 'auto',
              gridColumn: desktopBanner ? '2' : undefined,
              gridRow: desktopBanner ? '1 / span 2' : undefined,
              alignSelf: desktopBanner ? 'center' : undefined,
            }}
          >
            <button
              className="xp-consent-action"
              type="button"
              onClick={handleRejectAll}
              disabled={busy}
              style={{
                minHeight: compactMobileBanner ? '32px' : desktopBanner ? '30px' : '34px',
                padding: compactMobileBanner ? '5px 4px' : '6px 8px',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-primary)',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: compactMobileBanner ? '10.5px' : isMobile || desktopBanner ? '12px' : '13px',
                cursor: busy ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              {!compactMobileBanner && <X size={14} aria-hidden="true" />}
              {t('cookieConsent.rejectAll', 'Reject All')}
            </button>
            <button
              className="xp-consent-action"
              type="button"
              onClick={handleAcceptAll}
              disabled={busy}
              style={{
                minHeight: compactMobileBanner ? '32px' : desktopBanner ? '30px' : '34px',
                padding: compactMobileBanner ? '5px 4px' : '6px 8px',
                background: 'var(--accent-solid)',
                color: 'white',
                border: '1px solid var(--accent-solid)',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: compactMobileBanner ? '10.5px' : isMobile || desktopBanner ? '12px' : '13px',
                cursor: busy ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              {!compactMobileBanner && <Check size={14} aria-hidden="true" />}
              {t('cookieConsent.acceptAll', 'Accept All')}
            </button>
            <button
              className="xp-consent-action"
              type="button"
              onClick={() => setMode('customize')}
              disabled={busy}
              style={{
                minHeight: compactMobileBanner ? 32 : isMobile ? 34 : desktopBanner ? 30 : 34,
                padding: compactMobileBanner ? '5px 3px' : '6px 8px',
                background: 'transparent',
                color: 'var(--text-secondary)',
                border: '1px solid var(--border-primary)',
                borderRadius: '10px',
                fontSize: compactMobileBanner ? '10px' : isMobile || desktopBanner ? '12px' : '13px',
                fontWeight: 500,
                cursor: busy ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: compactMobileBanner ? '2px' : '4px',
              }}
            >
              {t('cookieConsent.customize', 'Customize')}
              {!compactMobileBanner && <ChevronRight size={14} aria-hidden="true" />}
            </button>
          </div>
        </>
      )}

      {mode === 'customize' && (
        <>
          <p
            id="cookie-consent-customize-description"
            style={{
              fontSize: '13px',
              lineHeight: 1.6,
              color: 'var(--text-secondary)',
              margin: '0 0 16px 0',
            }}
          >
            {t(
              'cookieConsent.customizeDescription',
              'Choose which categories of cookies you allow. Necessary cookies are required for the site to function.'
            )}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '16px' }}>
            {/* Necessary — always on */}
            <ConsentRow
              label={t('cookieConsent.categoryNecessary', 'Necessary')}
              description={t(
                'cookieConsent.categoryNecessaryDesc',
                'Required for authentication, session management, and security. Cannot be disabled.'
              )}
              checked={true}
              disabled={true}
              onChange={() => {}}
              lockedLabel={t('cookieConsent.alwaysActive', 'Always active')}
            />
            <ConsentRow
              label={t('cookieConsent.categoryAnalytics', 'Analytics')}
              description={t(
                'cookieConsent.categoryAnalyticsDesc',
                'Helps us understand how the product is used so we can improve it. Anonymous aggregate data only.'
              )}
              checked={draft.analytics}
              disabled={false}
              onChange={(v) => setDraft((d) => ({ ...d, analytics: v }))}
            />
            <ConsentRow
              label={t('cookieConsent.categoryMarketing', 'Marketing')}
              description={t(
                'cookieConsent.categoryMarketingDesc',
                'Used for personalized ads and to measure campaign effectiveness on external platforms.'
              )}
              checked={draft.marketing}
              disabled={false}
              onChange={(v) => setDraft((d) => ({ ...d, marketing: v }))}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setMode('banner')}
              disabled={busy}
              style={{
                padding: '10px 16px',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-primary)',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: '14px',
                cursor: busy ? 'wait' : 'pointer',
              }}
            >
              {t('cookieConsent.back', 'Back')}
            </button>
            <button
              type="button"
              onClick={handleSaveGranular}
              disabled={busy}
              style={{
                padding: '10px 16px',
                background: 'var(--accent-solid)',
                color: 'white',
                border: '1px solid var(--accent-solid)',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: '14px',
                cursor: busy ? 'wait' : 'pointer',
              }}
            >
              {t('cookieConsent.savePreferences', 'Save Preferences')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

interface ConsentRowProps {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
  lockedLabel?: string;
}

const ConsentRow: React.FC<ConsentRowProps> = ({ label, description, checked, disabled, onChange, lockedLabel }) => {
  const id = `consent-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '12px',
        background: 'var(--bg-tertiary)',
        borderRadius: '10px',
      }}
    >
      <div style={{ flex: 1 }}>
        <label
          htmlFor={id}
          style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: '2px',
          }}
        >
          {label}
        </label>
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{description}</div>
      </div>
      {disabled && lockedLabel ? (
        <span
          style={{
            fontSize: '11px',
            color: 'var(--text-secondary)',
            background: 'var(--bg-secondary)',
            padding: '4px 8px',
            borderRadius: '6px',
            whiteSpace: 'nowrap',
            alignSelf: 'center',
          }}
          aria-label={lockedLabel}
        >
          {lockedLabel}
        </span>
      ) : (
        <input
          id={id}
          type="checkbox"
          className="xp-consent-toggle"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          aria-label={label}
          style={{ flexShrink: 0, alignSelf: 'center' }}
        />
      )}
    </div>
  );
};

export default CookieConsent;
